import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  LocalHospital as LocalHospitalIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import athenaService from '../services/athenaService';
import ATHENA_CONFIG from '../config/api.config.js';

const TeamManager = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (providers.length > 0) {
      applyFilters();
    }
  }, [providers, searchTerm, filterType]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const providersData = await athenaService.getAllProviders();
      
      // Sort providers by last name
      const sortedProviders = providersData.sort((a, b) => {
        if (a.lastname && b.lastname) {
          return a.lastname.localeCompare(b.lastname);
        }
        return 0;
      });
      
      setProviders(sortedProviders);
      setFilteredProviders(sortedProviders);
      
      // Select the first provider by default if none is selected
      if (sortedProviders.length > 0 && !selectedProvider) {
        setSelectedProvider(sortedProviders[0]);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load providers',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...providers];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(provider => 
        (provider.firstname && provider.firstname.toLowerCase().includes(term)) ||
        (provider.lastname && provider.lastname.toLowerCase().includes(term)) ||
        (provider.displayname && provider.displayname.toLowerCase().includes(term)) ||
        (provider.specialty && provider.specialty.toLowerCase().includes(term))
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(provider => {
        if (filterType === 'person') {
          return provider.entitytype === 'Person';
        } else if (filterType === 'nonperson') {
          return provider.entitytype === 'Non-person entity';
        } else if (filterType === 'billable') {
          return provider.billable === true;
        }
        return true;
      });
    }
    
    setFilteredProviders(filtered);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setEditingProvider(null);
  };

  const handleEditProvider = () => {
    // Ensure all fields are properly initialized
    const providerToEdit = {
      ...selectedProvider,
      sex: selectedProvider.sex || '',
      providerusername: selectedProvider.providerusername || ''
    };
    setEditingProvider(providerToEdit);
  };

  const handleInputChange = (field, value) => {
    setEditingProvider(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelEdit = () => {
    setEditingProvider(null);
  };

  const handleSaveProvider = async () => {
    setSaving(true);
    try {
      // Only include fields that are displayed in the edit form
      const updateData = {
        practiceid: ATHENA_CONFIG.practiceId,
        providerid: editingProvider.providerid,
        username: editingProvider.providerusername || '',
        firstname: editingProvider.firstname || '',
        lastname: editingProvider.lastname || '',
        schedulingname: editingProvider.schedulingname || '',
        specialty: editingProvider.specialty || '',
        hideinportal: editingProvider.hideinportal || false,
        billable: editingProvider.billable || false,
        specialtyid: editingProvider.specialtyid || '',
        providertype: editingProvider.providertype || '',
        providertypeid: editingProvider.providertypeid || '',
        signatureonfileflag: editingProvider.signatureonfileflag || false,
        entitytypeid: parseInt(editingProvider.entitytypeid || (editingProvider.entitytype === 'Person' ? 1 : 2)),
        sex: editingProvider.sex || '',
        npi: editingProvider.npi || '',
        providergroupid: editingProvider.providergrouppid || 1,
        billednamecase: editingProvider.billednamecase || `${editingProvider.lastname || ''}, ${editingProvider.firstname || ''}`,
        ansicode: editingProvider.ansispecialtycode || 1,
        // Include createencounteroncheckin only if it's displayed in the form
        ...(editingProvider.entitytype === 'Person' ? { createencounteroncheckin: editingProvider.createencounteroncheckin || false } : {})
      };

      // Call the athenaService method to update the provider
      const result = await athenaService.updateProvider(
        updateData.practiceid,
        updateData.providerid,
        updateData
      );

      if (result) {
        setSnackbar({
          open: true,
          message: 'Provider updated successfully',
          severity: 'success'
        });

        // Refresh providers and update selected provider
        await fetchProviders();
        const updatedProvider = providers.find(prov => prov.providerid === editingProvider.providerid);
        if (updatedProvider) {
          setSelectedProvider(updatedProvider);
        }
        
        setEditingProvider(null);
      } else {
        throw new Error('Failed to update provider');
      }
    } catch (error) {
      console.error('Error updating provider:', error);
      setSnackbar({
        open: true,
        message: `Failed to update provider: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const getProviderInitials = (provider) => {
    if (provider.firstname && provider.lastname) {
      return `${provider.firstname.charAt(0)}${provider.lastname.charAt(0)}`;
    } else if (provider.lastname) {
      return provider.lastname.charAt(0);
    }
    return '?';
  };

  const getProviderTypeChip = (provider) => {
    if (provider.entitytype === 'Person') {
      return (
        <Chip 
          size="small" 
          label={provider.providertype || "Provider"} 
          color="primary" 
          variant="outlined"
          sx={{ marginRight: 1 }}
        />
      );
    } else {
      return (
        <Chip 
          size="small" 
          label="Non-Person Entity" 
          color="secondary" 
          variant="outlined"
          sx={{ marginRight: 1 }}
        />
      );
    }
  };

  if (loading && providers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, height: 'calc(100vh - 10px)', overflow: 'auto' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: '100%', 
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 0,
          borderBottom: `1px solid #e0e0e0`,
          mb: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Team Manager</Typography>
        </Box>
      </Paper>
      
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 3,
          bgcolor: 'white',
          borderRadius: 0,
          borderBottom: `1px solid #e0e0e0`,
          mb: 3
        }}
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          Manage your practice team. Update provider information.
        </Typography>
        
        {/* Search and Filter */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name or specialty"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-type-label">Filter By</InputLabel>
              <Select
                labelId="filter-type-label"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Filter By"
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Providers</MenuItem>
                <MenuItem value="person">Individuals</MenuItem>
                <MenuItem value="nonperson">Non-Person Entities</MenuItem>
                <MenuItem value="billable">Billable Providers</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {/* Providers List */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                border: '1px solid #e0e0e0', 
                height: 'calc(100vh - 130px)', 
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto'
              }}
            >
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #e0e0e0', 
                bgcolor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle1" fontWeight="bold">Providers</Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {filteredProviders.length > 0 ? (
                  filteredProviders.map((provider, index) => (
                    <React.Fragment key={provider.providerid}>
                      {index > 0 && <Divider />}
                      <ListItemButton 
                        selected={selectedProvider?.providerid === provider.providerid}
                        onClick={() => handleProviderSelect(provider)}
                        sx={{ 
                          position: 'relative'
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: provider.entitytype === 'Person' ? 'primary.main' : 'secondary.main',
                            width: 40,
                            height: 40
                          }}
                        >
                          {getProviderInitials(provider)}
                        </Avatar>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
                                {provider.displayname || `${provider.firstname || ''} ${provider.lastname || ''}`}
                              </Typography>
                              {provider.billable && (
                                <Tooltip title="Billable Provider">
                                  <VerifiedUserIcon 
                                    color="success" 
                                    fontSize="small" 
                                    sx={{ ml: 1 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {provider.specialty && (
                                <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                                  {provider.specialty}
                                </Typography>
                              )}
                              <Box sx={{ mt: 0.5 }}>
                                {getProviderTypeChip(provider)}
                              </Box>
                            </Box>
                          }
                        />
                        <IconButton 
                          size="small"
                          color="primary"
                          className="edit-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProvider(provider);
                            handleEditProvider();
                          }}
                          sx={{ 
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            position: 'absolute',
                            right: 8
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    </React.Fragment>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No providers match your search
                    </Typography>
                  </Box>
                )}
              </List>
            </Paper>
          </Grid>
          
          {/* Provider Details */}
          <Grid item xs={12} md={8}>
            {selectedProvider ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  p: 3,
                  height: 'calc(100vh - 130px)',
                  overflow: 'auto'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h5">
                    {editingProvider ? 'Edit Provider' : 'Provider Details'}
                  </Typography>
                  
                  {!editingProvider ? (
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<EditIcon />}
                      onClick={handleEditProvider}
                      sx={{ display: 'none' }}  // Hide the edit button
                    >
                      Edit
                    </Button>
                  ) : (
                    <Box>
                      <Button 
                        variant="outlined"
                        onClick={handleCancelEdit}
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSaveProvider}
                        disabled={saving}
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {editingProvider ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editingProvider.firstname || ''}
                        onChange={(e) => handleInputChange('firstname', e.target.value)}
                        disabled={editingProvider.entitytype !== 'Person'}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editingProvider.lastname || ''}
                        onChange={(e) => handleInputChange('lastname', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Scheduling Name"
                        value={editingProvider.schedulingname || ''}
                        onChange={(e) => handleInputChange('schedulingname', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Specialty"
                        value={editingProvider.specialty || ''}
                        onChange={(e) => handleInputChange('specialty', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Provider Type"
                        value={editingProvider.providertype || ''}
                        onChange={(e) => handleInputChange('providertype', e.target.value)}
                      />
                    </Grid>
                    
                    {editingProvider.entitytype === 'Person' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Billable</InputLabel>
                          <Select
                            value={editingProvider.billable}
                            label="Billable"
                            onChange={(e) => handleInputChange('billable', e.target.value)}
                          >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Hide in Portal</InputLabel>
                        <Select
                          value={editingProvider.hideinportal}
                          label="Hide in Portal"
                          onChange={(e) => handleInputChange('hideinportal', e.target.value)}
                        >
                          <MenuItem value={true}>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {editingProvider.entitytype === 'Person' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Create Encounter on Check-in</InputLabel>
                          <Select
                            value={editingProvider.createencounteroncheckin}
                            label="Create Encounter on Check-in"
                            onChange={(e) => handleInputChange('createencounteroncheckin', e.target.value)}
                          >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    
                    {editingProvider.supervisingproviderid && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Supervising Provider ID"
                          value={editingProvider.supervisingproviderid || ''}
                          onChange={(e) => handleInputChange('supervisingproviderid', e.target.value)}
                        />
                      </Grid>
                    )}
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="NPI"
                        value={editingProvider.npi || ''}
                        onChange={(e) => handleInputChange('npi', e.target.value)}
                      />
                    </Grid>
                    
                    {editingProvider.entitytype === 'Person' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Sex</InputLabel>
                          <Select
                            value={editingProvider.sex || ''}
                            label="Sex"
                            onChange={(e) => handleInputChange('sex', e.target.value)}
                          >
                            <MenuItem value="">Not Specified</MenuItem>
                            <MenuItem value="M">Male</MenuItem>
                            <MenuItem value="F">Female</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Provider Username"
                        value={editingProvider.providerusername || ''}
                        onChange={(e) => handleInputChange('providerusername', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Provider Type ID"
                        value={editingProvider.providertypeid || ''}
                        onChange={(e) => handleInputChange('providertypeid', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Entity Type ID</InputLabel>
                        <Select
                          value={editingProvider.entitytypeid || (editingProvider.entitytype === 'Person' ? 1 : 2)}
                          label="Entity Type ID"
                          onChange={(e) => handleInputChange('entitytypeid', parseInt(e.target.value))}
                        >
                          <MenuItem value={1}>Person (1)</MenuItem>
                          <MenuItem value={2}>Non-Person Entity (2)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Signature on File</InputLabel>
                        <Select
                          value={editingProvider.signatureonfileflag || false}
                          label="Signature on File"
                          onChange={(e) => handleInputChange('signatureonfileflag', e.target.value)}
                        >
                          <MenuItem value={true}>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: selectedProvider.entitytype === 'Person' ? 'primary.main' : 'secondary.main',
                            width: 64,
                            height: 64
                          }}
                        >
                          {getProviderInitials(selectedProvider)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">
                            {selectedProvider.displayname || `${selectedProvider.firstname || ''} ${selectedProvider.lastname || ''}`}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {getProviderTypeChip(selectedProvider)}
                            {selectedProvider.specialty && (
                              <Chip 
                                size="small" 
                                label={selectedProvider.specialty} 
                                color="default" 
                                variant="outlined"
                              />
                            )}
                            {selectedProvider.billable && (
                              <Chip 
                                size="small" 
                                icon={<VerifiedUserIcon />} 
                                label="Billable" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Provider ID</Typography>
                          <Typography variant="body1">{selectedProvider.providerid}</Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Scheduling Name</Typography>
                          <Typography variant="body1">{selectedProvider.schedulingname || 'N/A'}</Typography>
                        </Grid>
                        
                        {selectedProvider.npi && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">NPI</Typography>
                            <Typography variant="body1">{selectedProvider.npi}</Typography>
                          </Grid>
                        )}
                        
                        {selectedProvider.supervisingproviderid && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">Supervising Provider</Typography>
                            <Typography variant="body1">ID: {selectedProvider.supervisingproviderid}</Typography>
                          </Grid>
                        )}
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Hidden in Portal</Typography>
                          <Typography variant="body1">{selectedProvider.hideinportal ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        
                        {selectedProvider.entitytype === 'Person' && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">Create Encounter on Check-in</Typography>
                            <Typography variant="body1">{selectedProvider.createencounteroncheckin ? 'Yes' : 'No'}</Typography>
                          </Grid>
                        )}
                        
                        {selectedProvider.homedepartment && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">Home Department</Typography>
                            <Typography variant="body1">{selectedProvider.homedepartment}</Typography>
                          </Grid>
                        )}
                        
                        {selectedProvider.ansispecialtycode && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">ANSI Specialty Code</Typography>
                            <Typography variant="body1">{selectedProvider.ansinamecode || selectedProvider.ansispecialtycode}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            ) : (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  p: 3,
                  height: 'calc(100vh - 110px)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select a provider to view details
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamManager;
